'use client'

import { useState, useEffect, Suspense } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { useAuth } from '@/components/providers/supabase-auth-provider'
import { useToast } from '@/components/ui/use-toast'
import Image from 'next/image'
import Link from 'next/link'
import { Loader2, AlertCircle, Eye, EyeOff, ArrowRight, CheckCircle, Sparkles } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useRouter } from 'next/navigation'
import { FaGoogle } from 'react-icons/fa'
import { motion, AnimatePresence } from 'framer-motion'
import { TRIAL_CONFIG } from '@/config/subscriptions'

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
}

const staggerChildren = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
}

function SignUpContent() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [fullName, setFullName] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [agreedToTerms, setAgreedToTerms] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)
  const { signUp, signInWithGoogle, user } = useAuth()
  const { toast } = useToast()
  const router = useRouter()

  // Password strength indicators
  const [passwordStrength, setPasswordStrength] = useState({
    hasMinLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecialChar: false
  })

  // Update password strength indicators
  useEffect(() => {
    setPasswordStrength({
      hasMinLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    })
  }, [password])

  // If user is already logged in, always auto-start trial and redirect to dashboard
  useEffect(() => {
    const handlePostSignup = async () => {
      if (user) {
        try {
          const response = await fetch('/api/trial/start', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
          })
          const data = await response.json()

          if (response.ok && data.success) {
            toast({
              title: 'Trial Started!',
              description: `Your ${TRIAL_CONFIG.durationDays}-day Professional trial has begun. Enjoy full access to all features!`,
            })
          }
          // Don't show error if trial can't start - user might already have subscription
        } catch (error) {
          console.error('Error auto-starting trial:', error)
        }
        router.push('/dashboard')
        router.refresh()
      }
    }

    handlePostSignup()
  }, [user, router, toast])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Basic form validation
    if (!email || !password || !confirmPassword || !fullName) {
      setErrorMessage('Please fill out all required fields')
      return
    }
    
    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match')
      return
    }
    
    if (!agreedToTerms) {
      setErrorMessage('You must agree to the terms and privacy policy')
      return
    }
    
    try {
      setIsLoading(true)
      setErrorMessage(null)
      
      await signUp(email, password, fullName, companyName)
      
      toast({
        title: 'Account created',
        description: 'Your account has been created successfully. Please check your email for verification.',
      })
      
    } catch (error: any) {
      console.error('Sign up error:', error)
      setErrorMessage(error.message || 'Failed to create account')
      
      toast({
        title: 'Sign Up Failed',
        description: 'Unable to create your account. Please check the form and try again.',
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true)
    setErrorMessage(null)
    try {
      await signInWithGoogle()
      // Redirect is handled by the OAuth callback
    } catch (error) {
      console.error('Google sign in error:', error)
      setErrorMessage('Failed to sign up with Google. Please try again.')
      toast({
        title: 'Google Sign-up Failed',
        description: 'Unable to sign up with Google. Please try again.',
        variant: 'destructive',
      })
      setIsGoogleLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen flex-1">
      <motion.div 
        className="flex flex-1 flex-col justify-center px-4 py-12 sm:px-6 lg:flex-none lg:px-20 xl:px-24"
        initial="hidden"
        animate="visible"
        variants={staggerChildren}
      >
        <div className="mx-auto w-full max-w-sm lg:w-96">
          <motion.div variants={fadeIn}>
            <motion.div
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
              className="mb-8 flex items-center"
            >
              <div className="p-2 bg-gradient-to-br from-primary/80 to-primary/30 rounded-lg shadow-lg">
                <h1 className="text-3xl font-bold text-white">
                  <span className="opacity-90">Gov</span>
                  <span className="opacity-100">Con</span>
                  <motion.span 
                    initial={{ opacity: 0.7 }}
                    animate={{ opacity: [0.7, 1, 0.7] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    className="ml-1 text-white"
                  >
                    Data
                  </motion.span>
                </h1>
              </div>
            </motion.div>
            <h2 className="mt-2 text-2xl font-bold leading-9 tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-primary to-primary/60">
              Start Your Free Trial
            </h2>
            <div className="mt-3 flex items-center gap-2 text-sm text-emerald-600 bg-emerald-50 dark:bg-emerald-950/30 px-3 py-2 rounded-lg">
              <Sparkles className="h-4 w-4" />
              <span>{TRIAL_CONFIG.durationDays}-day Professional trial • No credit card required</span>
            </div>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              Already have an account?{' '}
              <Link href="/login" className="font-semibold text-primary hover:text-primary/80 transition-colors">
                Sign in
              </Link>
            </p>
          </motion.div>

          <motion.div className="mt-10" variants={fadeIn}>
            <div>
              <form onSubmit={handleSubmit} className="space-y-6">
                <AnimatePresence>
                  {errorMessage && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="rounded-md bg-destructive/10 p-4 text-sm text-destructive"
                    >
                      {errorMessage}
                    </motion.div>
                  )}
                </AnimatePresence>
                
                <motion.div variants={fadeIn}>
                  <label htmlFor="email" className="block text-sm font-medium leading-6">
                    Email address
                  </label>
                  <div className="mt-2">
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-11 transition-all duration-200 hover:border-primary focus:border-primary"
                    />
                  </div>
                </motion.div>

                <motion.div variants={fadeIn}>
                  <label htmlFor="fullName" className="block text-sm font-medium leading-6">
                    Full Name
                  </label>
                  <div className="mt-2">
                    <Input
                      id="fullName"
                      name="fullName"
                      type="text"
                      autoComplete="name"
                      required
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="h-11 transition-all duration-200 hover:border-primary focus:border-primary"
                    />
                  </div>
                </motion.div>

                <motion.div variants={fadeIn}>
                  <label htmlFor="companyName" className="block text-sm font-medium leading-6">
                    Company Name (Optional)
                  </label>
                  <div className="mt-2">
                    <Input
                      id="companyName"
                      name="companyName"
                      type="text"
                      autoComplete="organization"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      className="h-11 transition-all duration-200 hover:border-primary focus:border-primary"
                    />
                  </div>
                </motion.div>

                <motion.div variants={fadeIn}>
                  <label htmlFor="password" className="block text-sm font-medium leading-6">
                    Password
                  </label>
                  <div className="mt-2 relative">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-11 transition-all duration-200 hover:border-primary focus:border-primary"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground transition-colors"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <div className="mt-2 space-y-2">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className={`flex items-center gap-2 ${passwordStrength.hasMinLength ? 'text-green-500' : 'text-muted-foreground'}`}>
                        <CheckCircle className={`h-4 w-4 ${passwordStrength.hasMinLength ? 'opacity-100' : 'opacity-40'}`} />
                        <span>8+ characters</span>
                      </div>
                      <div className={`flex items-center gap-2 ${passwordStrength.hasUpperCase ? 'text-green-500' : 'text-muted-foreground'}`}>
                        <CheckCircle className={`h-4 w-4 ${passwordStrength.hasUpperCase ? 'opacity-100' : 'opacity-40'}`} />
                        <span>Uppercase</span>
                      </div>
                      <div className={`flex items-center gap-2 ${passwordStrength.hasLowerCase ? 'text-green-500' : 'text-muted-foreground'}`}>
                        <CheckCircle className={`h-4 w-4 ${passwordStrength.hasLowerCase ? 'opacity-100' : 'opacity-40'}`} />
                        <span>Lowercase</span>
                      </div>
                      <div className={`flex items-center gap-2 ${passwordStrength.hasNumber ? 'text-green-500' : 'text-muted-foreground'}`}>
                        <CheckCircle className={`h-4 w-4 ${passwordStrength.hasNumber ? 'opacity-100' : 'opacity-40'}`} />
                        <span>Number</span>
                      </div>
                    </div>
                  </div>
                </motion.div>

                <motion.div variants={fadeIn}>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium leading-6">
                    Confirm Password
                  </label>
                  <div className="mt-2">
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      autoComplete="new-password"
                      required
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="h-11 transition-all duration-200 hover:border-primary focus:border-primary"
                    />
                  </div>
                </motion.div>

                <motion.div variants={fadeIn} className="flex items-center">
                  <Checkbox 
                    id="terms" 
                    checked={agreedToTerms}
                    onCheckedChange={(checked) => setAgreedToTerms(checked === true)}
                    className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                  />
                  <label htmlFor="terms" className="ml-2 block text-sm text-muted-foreground">
                    I agree to the{' '}
                    <Link href="/terms-of-service" className="text-primary hover:text-primary/80 transition-colors hover:underline">
                      Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link href="/privacy-policy" className="text-primary hover:text-primary/80 transition-colors hover:underline">
                      Privacy Policy
                    </Link>
                  </label>
                </motion.div>

                <motion.div variants={fadeIn}>
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="relative flex w-full justify-center h-11 transition-all duration-200"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating account...
                      </>
                    ) : (
                      <span className="flex items-center">
                        Start free trial
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </span>
                    )}
                    
                    {/* Hover effect overlay */}
                    <span className="absolute inset-0 bg-gradient-to-tr from-primary/0 to-primary/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </Button>
                </motion.div>
              </form>
            </div>

            <motion.div className="mt-6" variants={fadeIn}>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-sm font-medium leading-6">
                  <span className="bg-background px-6 text-muted-foreground">Or continue with</span>
                </div>
              </div>

              <motion.div 
                className="mt-6"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Button
                  onClick={handleGoogleSignIn}
                  disabled={isGoogleLoading}
                  variant="outline"
                  className="relative flex w-full items-center justify-center h-11 hover:border-primary transition-all duration-200 group"
                >
                  {isGoogleLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Image 
                        src="/icons/google.svg" 
                        alt="Google logo" 
                        width={20} 
                        height={20} 
                        className="mr-2" 
                      />
                      <span className="group-hover:text-primary transition-colors">Sign up with Google</span>
                    </>
                  )}
                  
                  {/* Hover effect overlay */}
                  <span className="absolute inset-0 bg-gradient-to-tr from-primary/0 to-primary/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </Button>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>
      
      <motion.div 
        className="relative hidden w-0 flex-1 lg:block"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        <div className="absolute inset-0 h-full w-full bg-gradient-to-br from-primary/90 to-primary/40 flex items-center justify-center overflow-hidden">
          <div className="relative w-full max-w-md p-8 text-white z-10">
            <motion.h2 
              className="text-4xl font-bold mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              Join GovCon Data
            </motion.h2>
            <motion.p 
              className="text-xl mb-8 text-white/90"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              Join our platform to access critical insights and analytics for government contracting opportunities.
            </motion.p>
            
            {/* Animated background elements */}
            <div className="absolute inset-0 opacity-10">
              <motion.div
                className="absolute top-10 left-10 w-20 h-20 rounded-full bg-white"
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.3, 0.6, 0.3],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
              <motion.div
                className="absolute bottom-20 right-10 w-32 h-32 rounded-full bg-white"
                animate={{
                  scale: [1, 1.3, 1],
                  opacity: [0.2, 0.5, 0.2],
                }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1
                }}
              />
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default function SignUpPage() {
  return (
    <Suspense fallback={
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    }>
      <SignUpContent />
    </Suspense>
  )
} 